import { NgTemplateOutlet } from '@angular/common'
import {
    Component,
    TemplateRef,
    inject,
    ChangeDetectionStrategy,
    NgZone,
    signal,
    input,
    output,
    effect,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { TbDragDropEvent } from '@components-library/tb-drag-and-drop/drag-drop.types'
import { TbDragDirective } from '@components-library/tb-drag-and-drop/tb-drag.directive'
import { TbDropContainerDirective } from '@components-library/tb-drag-and-drop/tb-drop-container.directive'
import { BreakpointService } from '@components-library/services/breakpoint.service'
import { TbColumn } from '@components-library/tb-board/board.model'
import { TbBoardColumnComponent } from '@components-library/tb-board/tb-board-column/tb-board-column.component'
import { TbMoreMenuComponent } from '@components-library/tb-more-menu/tb-more-menu.component'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { RecycleViewElementsState } from '@nomad2102npm/cdk/collections'
import { Subscription } from 'rxjs'
import { TbCheckboxComponent } from '../../tb-checkbox/checkbox.component'

import { TbTooltipComponent } from '@components-library/tb-tooltip/tb-tooltip-component/tb-tooltip.component'
import { findItemInColumns, isBoardCardData } from '../board-utils'

@UntilDestroy()
@Component({
    selector: 'app-tb-board-card',
    templateUrl: './tb-board-card.component.html',
    styleUrls: ['./tb-board-card.component.sass'],
    imports: [
        TbCheckboxComponent,
        NgTemplateOutlet,
        TbMoreMenuComponent,
        TbTooltipComponent,
        TbDragDirective,
        TbDropContainerDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TbBoardCardComponent<Item extends { guid: string }> {
    private readonly ngZone = inject(NgZone)
    private readonly breakpointService = inject(BreakpointService)
    private readonly boardColumnComponent = inject(TbBoardColumnComponent)

    readonly item = input.required<Item>()
    readonly index = input.required<number>()
    readonly key = input.required<keyof Item>()
    readonly columns = input.required<TbColumn<Item>[]>()

    readonly title = input('')
    readonly selected = input(false)
    readonly selectedItems = input<Item[]>([])
    readonly disableDrop = input(false)

    readonly moreMenu = input.required<TemplateRef<any>>()
    readonly favorite = input.required<TemplateRef<any>>()

    readonly selectCard = output<boolean>()

    dragged = signal(false)

    isSmallSize = toSignal(this.breakpointService.displaySize$)

    private readonly scrollingStore = inject(RecycleViewElementsState)

    scrollingStoreSubscriber?: Subscription

    constructor() {
        effect(() => {
            if (this.scrollingStoreSubscriber) this.scrollingStoreSubscriber.unsubscribe()

            if (!this.item().guid) return

            this.scrollingStoreSubscriber = this.scrollingStore
                .subscribe<{ dragged?: boolean }>(this.item().guid)
                .pipe(untilDestroyed(this))
                .subscribe((state) => {
                    if (state && 'dragged' in state) {
                        this.dragged.set(state.dragged!)
                    } else {
                        this.dragged.set(false)
                    }
                })
        })
    }

    getMenuClasses(menuOpen: boolean): string | string[] {
        return menuOpen || this.isSmallSize()
            ? 'visible'
            : ['group-hover/board-card:visible', 'visible', 'md:invisible']
    }

    onDragStarted() {
        this.dragged.set(true)
        this.scrollingStore.add(this.item().guid, { dragged: true })
    }

    onDragEnded() {
        this.dragged.set(false)
    }

    onDrop(args: TbDragDropEvent) {
        const sourceData = args.source.data
        const targetData = args.self.data

        if (!isBoardCardData<Item>(sourceData) || !isBoardCardData<Item>(targetData)) return
        if (args.previousIndex === null || args.currentIndex === null) return
        const previousIndex = args.previousIndex
        const currentIndex = args.currentIndex

        this.ngZone.run(() => {
            this.applyDrop(
                sourceData.boardCardData,
                targetData.boardCardData,
                previousIndex,
                currentIndex,
            )
        })
    }

    getCardDragData() {
        return { boardCardData: this.item(), index: this.index() }
    }

    private applyDrop(
        sourceItem: Item,
        targetItem: Item,
        previousIndex: number,
        currentIndex: number,
    ) {
        const prevColumn = findItemInColumns(sourceItem.guid, this.columns())
        const targetColumn = findItemInColumns(targetItem.guid, this.columns())

        this.scrollingStore.remove(sourceItem.guid)

        if (!prevColumn || !targetColumn) return

        this.boardColumnComponent.dropCard({
            fromList: prevColumn.data,
            toList: targetColumn.data,
            prevIndex: previousIndex,
            index: currentIndex,
            data: sourceItem,
        })
    }
}
